import React from 'react';
import { LockIcon } from 'lucide-react';
import { Slide } from '../../components/deck/Slide';
import { Caption, Eyebrow, INK, Label, Reveal, Title } from '../../components/deck/Type';
import { MAP_IMAGE } from '../../data/logos';
import { DIRECTORY_SOURCE, platformHub, regions } from '../../data/ecosystemNetworks';
const SERIF = {
  fontFamily: 'var(--font-serif)'
} as const;

/**
 * Map calibration. The map image is treated as spanning MAP_LON × MAP_LAT;
 * the panel shows the VIEW window (North America → Europe). Adjust these if
 * pins drift from the artwork.
 */
const MAP_LON = {
  west: -180,
  east: 180
};
const MAP_LAT = {
  north: 84,
  south: -58
};
const VIEW = {
  west: -135,
  east: 38,
  north: 72
};
const lonSpan = MAP_LON.east - MAP_LON.west;
const latSpan = MAP_LAT.north - MAP_LAT.south;

/** lon/lat → % of the full map image. */
function project(lat: number, lon: number) {
  return {
    x: (lon - MAP_LON.west) / lonSpan * 100,
    y: (MAP_LAT.north - lat) / latSpan * 100
  };
}
const sources = [{
  name: 'Public',
  detail: 'Directories, filings, research, announcements',
  tone: INK.grey
}, {
  name: 'Licensed',
  detail: 'Paid datasets, used within licensing terms',
  tone: INK.gold
}, {
  name: 'Private',
  detail: 'Permissioned submissions, customer workspace data',
  tone: INK.violet
}, {
  name: 'Partners',
  detail: 'Direct contributions from activated networks',
  tone: INK.emerald
}];
const mechanism = [{
  level: 'Local community',
  detail: 'Maintains its companies, cohorts and opportunities'
}, {
  level: 'Parent network · Global Valley',
  detail: 'Approved information rolls up into directories'
}, {
  level: 'DiligenceGPT™',
  detail: 'Normalizes records, adds analytical context'
}];
const values = [{
  name: 'Access',
  tone: INK.blue,
  line: 'Direct ecosystem relationships surface information that stays fragmented across organizations.'
}, {
  name: 'Standardization',
  tone: INK.violet,
  line: 'Resolve company identities, reconcile sources, align financial, operating and risk metrics.'
}, {
  name: 'History',
  tone: INK.gold,
  line: 'Moving averages and longitudinal benchmarks for risks, comparables, valuations and KPIs by sector, stage and geography.'
}];
function arcPath(from: {
  x: number;
  y: number;
}, to: {
  x: number;
  y: number;
}) {
  const mx = (from.x + to.x) / 2;
  const my = (from.y + to.y) / 2 - Math.abs(to.x - from.x) * 0.35 - 1.2;
  return `M${from.x},${from.y} Q${mx},${my} ${to.x},${to.y}`;
}
function NetworkMap() {
  const hub = project(platformHub.lat, platformHub.lon);
  const widthPct = lonSpan / (VIEW.east - VIEW.west) * 100;
  const shiftX = (VIEW.west - MAP_LON.west) / lonSpan * 100;
  const shiftY = (MAP_LAT.north - VIEW.north) / latSpan * 100;
  return <div className="relative h-full w-full overflow-hidden border border-white/10 bg-[#050811]" data-id="element-194" data-mp="2djkzy5r17z39">
      <div className="absolute left-0 top-0" style={{
      width: `${widthPct}%`,
      transform: `translate(-${shiftX}%, -${shiftY}%)`
    }} data-id="element-195" data-mp="29ln9z0pft8yt">
        <img src={MAP_IMAGE} alt="World map with Canada, the US Midwest and Europe highlighted as ecosystem data regions" className="block h-auto w-full opacity-50" data-id="element-196" data-mp="k1ng5tkmt79k" />

        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full" aria-hidden data-id="element-197" data-mp="t4ohlgyjya6w">
          {regions.map(r => r.hubs.filter((_, i) => i % 3 === 0).map(h => <path key={`${r.id}-${h.city}`} d={arcPath(project(h.lat, h.lon), hub)} fill="none" stroke={r.tone} strokeOpacity={0.55} strokeWidth={1.2} strokeDasharray="3 3" vectorEffect="non-scaling-stroke" data-id="element-198" data-mp="5eltfbxrdodk" />))}
        </svg>

        {regions.map(r => r.hubs.map(h => {
        const p = project(h.lat, h.lon);
        return <span key={`${r.id}-${h.city}-dot`} className="absolute h-[0.55cqw] w-[0.55cqw] -translate-x-1/2 -translate-y-1/2 rounded-full" style={{
          left: `${p.x}%`,
          top: `${p.y}%`,
          background: r.tone,
          boxShadow: `0 0 0.6cqw ${r.tone}`
        }} aria-hidden data-id="element-199" data-mp="1p7lua4f85wo6" />;
      }))}

        {regions.map(r => {
        const p = project(r.label.lat, r.label.lon);
        return <div key={`${r.id}-label`} className="absolute w-[12cqw] -translate-x-1/2 -translate-y-1/2 border border-white/12 bg-[#050811]/85 px-[0.8cqw] py-[1cqh]" style={{
          left: `${p.x}%`,
          top: `${p.y}%`
        }} data-id="element-200" data-mp="230v589itmq8t">
              <div className="flex items-center gap-[0.5cqw]" data-id="element-201" data-mp="mmd1nguzhk3m">
                <span className="h-[0.5cqw] w-[0.5cqw] rounded-full" style={{
              background: r.tone
            }} data-id="element-202" data-mp="iljbshxzdv83" />
                <span className="text-[0.86cqw] text-[#F4F7FC]" data-id="element-203" data-mp="8hl0d8hbhyna">{r.name}</span>
              </div>
              <p className="mt-[0.6cqh] text-[1.6cqw] leading-none" style={{
            ...SERIF,
            color: r.tone
          }} data-id="element-204" data-mp="cj6lfh409j8k">
                {r.directoryCoverage ?? '—'}
              </p>
              <p className="mt-[0.5cqh] text-[0.62cqw] uppercase tracking-[0.16em] text-[#8B95A8]" data-id="element-205" data-mp="3uwxevnaqx52x">
                Directory coverage
              </p>
              <p className="mt-[0.4cqh] text-[0.62cqw] uppercase tracking-[0.16em] text-[#5E6779]" data-id="element-206" data-mp="3kyhxic1ita6m">
                Active contributors · {r.activeContributors ?? 'to confirm'}
              </p>
            </div>;
      })}

        <div className="absolute -translate-x-1/2 -translate-y-1/2" style={{
        left: `${hub.x}%`,
        top: `${hub.y}%`
      }} data-id="element-207" data-mp="3bzw75upt4qva">
          <span className="block h-[1.5cqw] w-[1.5cqw] rounded-full border-2 border-[#D8A55A] bg-[#050811]" data-id="element-208" data-mp="1a6yi89z5iifm" />
        </div>
        <div className="absolute whitespace-nowrap border border-[#D8A55A]/50 bg-[#050811]/90 px-[0.7cqw] py-[0.6cqh] text-[0.8cqw] text-[#F4F7FC]" style={{
        left: `${hub.x + 0.6}%`,
        top: `${hub.y + 1.6}%`
      }} data-id="element-209" data-mp="1f6b68y1rdvjs">
          {platformHub.name}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 flex items-center gap-[1.6cqw] border-t border-white/10 bg-[#050811]/90 px-[1.2cqw] py-[1.1cqh]" data-id="element-210" data-mp="13q6iy1l5i5en">
        <span className="flex items-center gap-[0.5cqw] text-[0.74cqw] text-[#AEB7C7]" data-id="element-211" data-mp="ytpv78hv5t23">
          <span className="h-[0.5cqw] w-[0.5cqw] rounded-full bg-[#AEB7C7]" data-id="element-212" data-mp="1wekhqjk2d5r9" /> Indexed organization
        </span>
        <span className="flex items-center gap-[0.5cqw] text-[0.74cqw] text-[#AEB7C7]" data-id="element-213" data-mp="1ridkdf5qh2kx">
          <span className="h-[0.8cqw] w-[0.8cqw] rounded-full border border-[#D8A55A]" data-id="element-214" data-mp="1mnpw9hen6a6v" /> Active data contributor
        </span>
        <span className="ml-auto text-[0.7cqw] text-[#6B7588]" data-id="element-215" data-mp="2f5v4coirawdc">
          Directory inclusion ≠ partnership or active data feed
        </span>
      </div>
    </div>;
}
export function DataMoatNetworkSlide() {
  return <Slide source={DIRECTORY_SOURCE} data-id="element-216" data-mp="2hqt344t58gj4">
      <div className="flex items-start justify-between gap-[3cqw]" data-id="element-217" data-mp="2hrui2kda5n64">
        <div className="max-w-[44%]" data-id="element-218" data-mp="2mokyj8szyks2">
          <Eyebrow data-id="element-219" data-mp="2mpmjq63q8nni">The moat</Eyebrow>
          <Title className="mt-[2cqh]" data-id="element-220" data-mp="2roqqyr73cbb7">Data is our Moat</Title>
          <Caption className="mt-[1.2cqh]" data-id="element-221" data-mp="2rn60pdrt7ej5">
            Activated ecosystem networks are already supplying data beyond the public web.
          </Caption>
        </div>
        <p className="max-w-[48%] text-[1.2cqw] leading-[1.4] text-[#DCE3EE]" style={SERIF} data-id="element-222" data-mp="30o46o1lt5w4d">
          “We are already receiving siloed data directly from activated ecosystem
          channels — combining public, licensed, private and partner contributions
          into a standardized intelligence layer.”
        </p>
      </div>

      <div className="mt-[2.4cqh] grid min-h-0 flex-1 grid-cols-[57%_1fr] gap-[2cqw]" data-id="element-223" data-mp="3fg4kv1kw4eya">
        <NetworkMap data-id="element-224" data-mp="3keze6xghhlwn" />

        <div className="flex min-h-0 flex-col" data-id="element-225" data-mp="3kbbknyb1gzhx">
          <Label tone={INK.blue} data-id="element-226" data-mp="3ofrmnohj7mrv">Data sources</Label>
          <ul className="mt-[1cqh]" data-id="element-227" data-mp="3ocdnsq82tvp6">
            {sources.map(s => <li key={s.name} className="flex items-baseline gap-[1cqw] border-b border-white/8 py-[1cqh]" data-id="element-228" data-mp="dihb21gzj19l">
                <span className="w-[5.6cqw] shrink-0 text-[1cqw]" style={{
              color: s.tone
            }} data-id="element-229" data-mp="9krwejvv78e8">
                  {s.name}
                </span>
                <span className="text-[0.88cqw] leading-snug text-[#AEB7C7]" data-id="element-230" data-mp="iivgrcv45z4g">{s.detail}</span>
              </li>)}
          </ul>

          <div className="mt-[2.2cqh] flex items-center justify-between" data-id="element-231" data-mp="3qzgl2gcsw418">
            <Label tone={INK.emerald} data-id="element-232" data-mp="3m15ag3eolphy">Network mechanism</Label>
            <span className="flex items-center gap-[0.4cqw] text-[0.72cqw] text-[#8B95A8]" data-id="element-233" data-mp="4jcc4u1ih5ww">
              <LockIcon className="h-[0.8cqw] w-[0.8cqw]" strokeWidth={1.6} aria-hidden data-id="element-234" data-mp="3vya1ohndwb5m" />
              Permissions travel with the data
            </span>
          </div>
          <div className="mt-[1.2cqh] flex flex-col" data-id="element-235" data-mp="386uvgdshl9p3">
            {mechanism.map((m, i) => <React.Fragment key={m.level} data-id="element-236" data-mp="339cimcbcmabt" data-mpi={i}>
                {i > 0 && <span className="py-[0.3cqh] pl-[1cqw] text-[0.8cqw] leading-none text-[#3B8CFF]" aria-hidden data-id="element-237" data-mp="1iqiq3re9r47p">
                    ↓
                  </span>}
                <Reveal delay={0.08 * i} className={i === mechanism.length - 1 ? 'border border-[#3B8CFF]/50 bg-[#3B8CFF]/[0.08] px-[1cqw] py-[1.1cqh]' : 'border border-white/10 bg-white/[0.02] px-[1cqw] py-[1.1cqh]'} data-id="element-238" data-mp="19rf70nf3qot7">
                  <p className="text-[0.98cqw] text-[#F4F7FC]" data-id="element-239" data-mp="26f1z69557l0u">{m.level}</p>
                  <p className="mt-[0.3cqh] text-[0.82cqw] text-[#8B95A8]" data-id="element-240" data-mp="1sgv9vhe9etcs">{m.detail}</p>
                </Reveal>
              </React.Fragment>)}
          </div>
        </div>
      </div>

      <div className="mt-[2.4cqh] grid grid-cols-3 gap-[2cqw]" data-id="element-241" data-mp="h0n4773j71ww">
        {values.map((v, i) => <Reveal key={v.name} delay={0.2 + i * 0.05} className="border-t border-white/12 pt-[1.4cqh]" data-id="element-242" data-mp="m2w0odfm4m3r" data-mpi={i}>
            <p className="text-[1.3cqw] leading-none" style={{
          ...SERIF,
          color: v.tone
        }} data-id="element-243" data-mp="ye9uxro4okv2">
              {v.name}
            </p>
            <p className="mt-[0.9cqh] text-[0.86cqw] leading-snug text-[#AEB7C7]" data-id="element-244" data-mp="12h66wzj5xiq1">{v.line}</p>
          </Reveal>)}
      </div>

      <div className="mt-[1.8cqh] flex items-center gap-[1.2cqw]" data-id="element-245" data-mp="tm8c1mwgfvw2">
        <span className="h-px w-[2.2cqw] shrink-0 bg-[#D8A55A]" aria-hidden data-id="element-246" data-mp="epr12ws6gja1" />
        <p className="text-[1.05cqw] leading-snug text-[#F4F7FC]" style={SERIF} data-id="element-247" data-mp="erveq6fvowni">
          General-purpose AI models cannot access these permissioned data flows without
          direct connections. Our advantage compounds through access, standardization
          and accumulated history.
        </p>
      </div>
    </Slide>;
}